// Trail furniture in the 3D world (FURN-3D): the amenity points
// (meta.amenities → trails/amenities.json, the MAP-D `trailAmenities`) drawn
// as low-poly objects at their real positions — benches, fountains, fire
// pits, info boards, crosses, bins, toilets, swings, chargers, P signs …
// Owner: "all these objects should show as accurate as possible from the
// data without costing much".
//
// - One model per VARIANT (`variantOf`: the kind + the tags the bake carries:
//   backrest yes / no, covered, summit_cross / a gazetteer peak ≤ 80 m,
//   historic), built from boxes / beams / rings in a local frame (x = along,
//   y up, z = the FRONT), ≤ 60 tris a bench, 90 a table, 120 a fountain, 80
//   else. A saddle is no object (SKIP_KINDS).
// - Look (`lookOf`, FURN-T): OSM material (wood / metal / stone / concrete /
//   plastic, synonyms in MAT_WORD) and colour (#hex or a W3C name) recolour
//   a model's two material tones L (seat, panel, trough, bin …) and D
//   (frame, posts, column); a bench / table with two materials gets a wood
//   seat on the other material's frame. Untagged → the variant's own
//   colours, byte-identical to FURN-3D.
// - Orientation (`orientOf`): OSM `direction` first (the bearing the model's
//   front faces: a bench's seated person looks that way, a board's readable
//   face points there — OSM wiki Key:direction); else the nearest way
//   (RoadGraph, not rail) within 20 m — the long axis along it; a table,
//   rack, fountain, swing turns its BACK to the way, everything else its
//   front TO the way. A bench (benchFront): by a tarmac road faces it, else
//   downhill, on flat ground the nearest lake / river, else the way. No way → a deterministic yaw from the OSM id.
// - Text: real names on a shared atlas (a board's / map's name, a
//   milestone's "4 Km", a plaque's / artwork's name; the blue P, WC).
// - Height (`seatOf`, furniture-limits): the WALKABLE surface at the spot,
//   resolved by the Player's own ground query (`standOf` → Player._groundY
//   without the lake float + Player.headroomAt — no second resolver): what a
//   body at the data height stands on (terrain skin, a spans bed, a deck /
//   structure floor within the step), else a floor ≤ deck_reach_m over the
//   data height when it is nearer, and only levels with room for the item
//   under whatever is over them, measured from the seat itself (a girder
//   0.3 m over the ground is a roof: a bench cannot stand inside a low deck,
//   it climbs onto it — at most deck_reach_m + deck_depth_m over the data
//   height; a bench under a bridge with headroom stays under, and so does a
//   bus stop too tall for a footbridge's clearance). On the
//   terrain level: min over the footprint corners (standHeight) once its
//   tiles are in, so nothing floats or sinks on a slope.
// - Draw: ONE dynamic merged mesh — MeshBasicMaterial({vertexColors, map,
//   DoubleSide, fog}), the trail signposts' exact program (no new shader),
//   colours = linear × the objects.js fake-lambert face shade, tone-mapped.
//   Rebuilt from a 100 m grid only when the player moved > 25 m (or a
//   pending height snap, ≥ 1.5 s apart): near kinds ≤ 250 m, landmarks
//   ≤ 600 m (KIND_GATE). Same scene object in VR, flat and phone.
// - Optic (furniture-limits): while the focus wedge is in force a second
//   mesh on the focus layer (optic passes only; the naked pass unchanged)
//   draws the items past their naked gate inside the wedge out to
//   `opticRange` = gate × the view's magnification (capped optic_max_m),
//   nearest the apex first under optic_tri_budget, ONE draw.
// - DETAIL-1 street furniture (D1-FURN; amenities.json `street`, appended
//   after the pts by prepAmenities): bus stops (pole + the yellow name plate
//   on the atlas + a bus pictogram plate, perpendicular to the road; a glass
//   shelter when shelter=yes; a bench only when bench=yes and the bake found
//   no mapped bench there) and the point barriers (lift gate — also the TLM
//   road barrier of unknown kind —, field gate, swing → field gate, kissing
//   gate → a small gate, stile, bollard), spanning ACROSS the way, drawn
//   only (no collider: passable, owner ruling); near gate 250 m.
// - Inspect: `pick(ray, P, dist)` — the aim ray vs each drawn object's
//   oriented box (+0.3 m), else the aimed point ≤ 1.5 m from one → the
//   index into the amenity list (inspect.js → the MAP-D amenity card).
//
// The model / plan / grid / write functions are pure (node-importable;
// scripts/check-furniture.mjs); only `Furniture` and the atlas paint touch
// three / the DOM.
import * as THREE from 'three';

export const FURN = {
  near_m: 250,            // KIND_GATE near: benches, bins, tables …
  far_m: 600,             // KIND_GATE far: landmarks (crosses, fountains, boards …)
  step_m: 25,             // rebuild when the player moved this far
  cell_m: 100,            // spatial grid
  orient_r_m: 20, orient_dy_m: 12,   // the way that orients an object
  // a bench without OSM direction (owner 2026-10-12): by a tarmac road it
  // faces the road; else downhill (the valley) where the ground falls at
  // least slope_min over ±base_m; on flat ground the nearest lake / river
  // within water_r_m; else the way. Along a way the front stays across it
  // (the long axis along) when |n·dir| ≥ cross_min, else the raw direction.
  bench: { base_m: 12, slope_min: 0.06, water_r_m: 300, cross_min: 0.35 },
  peak_r_m: 80, peak_dz_m: 40,       // a cross this near a gazetteer peak is a summit cross
  pick_pad_m: 0.3,        // ray vs box padding
  near_pt_m: 1.5,         // the aimed point this near an object picks it
  resnap_ms: 1500,        // a pending height snap rebuilds at most this often
  snap_r_m: 128,          // always inside the resident L0 3 × 3 (128 m tiles) — a snap there is pending until it lands
  sink_m: 0.15,           // posts / legs reach below the ground point
  // seatOf (furniture-limits): a floor this far over the data height (the
  // bake's terrain y) outranks a farther one (a ramp deck over an open cut);
  // with no room under the sheet over the item, the floor up to this far
  // over that sheet's underside is the seat (a deck's depth: 0.92 m measured)
  // — only up to deck_reach_m + deck_depth_m (2.5 m) over the data height: a
  // higher deck is another level (a tall item under a footbridge stays down)
  deck_reach_m: 1.0, deck_depth_m: 1.5,
  // the optic layer (furniture-limits): the naked gates × the magnification
  // (opticRange), capped; ≤ this many tris in ONE draw, nearest the apex first
  optic_max_m: 6000, optic_tri_budget: 16000, optic_step: 1.12,
  slot_w: 256, slot_h: 64, atlas_w: 1024, atlas_max_h: 1024,   // text slots 4:1; a PAGE = 1024 × 1024 = 64 slots
  atlas_pages: 2,         // the furniture atlas: page 1 (slot 0 white + 63 texts) + page 2 (bus stop names …)
};

// the gate class per kind (KIND_GATE[k]; a historic milestone is near, any
// other historic far — gateOf)
export const KIND_GATE = {
  bench: 'near', waste_basket: 'near', picnic_table: 'near', firepit: 'near', bbq: 'near', toilets: 'near',
  playground: 'near', charging_station: 'near', parking: 'near', bicycle_parking: 'near', route_marker: 'near',
  survey_point: 'near',
  cross: 'far', drinking_water: 'far', spring: 'far', board: 'far', map: 'far', shelter: 'far', artwork: 'far',
  historic: 'far',
  // DETAIL-1 street furniture (amenities.json `street`)
  bus_stop: 'near', gate: 'near', swing_gate: 'near', kissing_gate: 'near', stile: 'near', lift_gate: 'near',
  bollard: 'near', barrier: 'near',
};
export const SKIP_KINDS = new Set(['saddle']);   // a pass has no object
export const TRI_CAP = { bench: 60, picnic_table: 90, drinking_water: 120, spring: 120, bus_stop: 140 };
export const triCap = (kind) => TRI_CAP[kind] || 80;
export const gateOf = (p) => (p.k === 'historic' && p.t && p.t.historic === 'milestone' ? 'near' : KIND_GATE[p.k] || null);
// +1: the front faces AWAY from the way; −1: TO it (the default — a bench
// too: the seated person looks at the path, owner 2026-10-12)
const FACE = { picnic_table: 1, bicycle_parking: 1, drinking_water: 1, playground: 1 };
const FREE = new Set(['firepit', 'survey_point']);   // no front: the id yaw
// spans ACROSS the way: the front (+z) runs along it, the long axis x across
export const ACROSS = new Set(['gate', 'swing_gate', 'kissing_gate', 'stile', 'lift_gate', 'bollard', 'barrier']);

// ---------------------------------------------------------------- colours
// sRGB hex → linear (the working space; THREE.Color does the same)
export function lin(hex) {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255].map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
}
const C = {
  wood: lin('#8a6a48'), woodD: lin('#5e4630'), metal: lin('#5d6166'), steel: lin('#9aa0a6'),
  stone: lin('#9a968c'), stoneD: lin('#77746d'), concrete: lin('#b8b3a8'), water: lin('#4f7fa3'),
  jet: lin('#a9c8dc'), ash: lin('#34302d'), roof: lin('#5a4535'), wall: lin('#d8d0bf'), bin: lin('#4d5c50'),
  red: lin('#b8352b'), black: lin('#222222'), yellow: lin('#F2C400'), blue: lin('#1f5fbf'), white: lin('#f2f2f2'),
  bronze: lin('#6e5530'), board: lin('#efe8d6'), mapc: lin('#cfdcb4'), screen: lin('#1d2a33'), green: lin('#2f7d4a'),
  rope: lin('#8b8b8b'), grate: lin('#2a2a2a'),
  concreteD: lin('#8f8b82'), plastic: lin('#4f6f58'), plasticD: lin('#36463b'),
  frame: lin('#4a4f55'), glass: lin('#b7c9cf'), ink: lin('#1a1a1a'),
};
// text styles: slot background = the face colour (sRGB, the atlas decodes)
export const STYLE = {
  board: { bg: '#efe8d6', fg: '#2b2620', px: 40, bold: true },
  stone: { bg: '#9a968c', fg: '#1e1d1b', px: 44, bold: true },
  plaque: { bg: '#6e5530', fg: '#f1e6c8', px: 38, bold: false },
  sign: { bg: '#1f5fbf', fg: '#ffffff', px: 58, bold: true },
  bus: { bg: '#F2C400', fg: '#1a1a1a', px: 40, bold: true },   // a Swiss stop plate: black on the yellow (C.yellow)
};
// a new text style for another atlas user (D1-BLD shop signs): {bg, fg (sRGB #hex), px, bold}
export function addStyle(name, st) { if (!STYLE[name]) STYLE[name] = { ...st }; return STYLE[name]; }

// objects.js Acc: fake-lambert per face — the same constants as the trail
// marks and the TLM decor, so a bench reads like the stake beside it
export function shadeOf(nx, ny, nz) { return 0.68 + 0.3 * Math.max(0, ny) + 0.09 * nx + 0.05 * nz; }

// ---------------------------------------------------------------- model kit
// local frame: x = along, y up (0 = the ground point), z = the front
class Mdl {
  constructor() { this.pos = []; this.nrm = []; this.col = []; this.uv = []; this.tx = []; this.idx = []; }
  get tris() { return this.idx.length / 3; }
  // a planar polygon (fan), unit normal n, colour c; U = slot-local uvs (a text face)
  poly(P, n, c, U = null) {
    const base = this.pos.length / 3;
    P.forEach((p, k) => {
      this.pos.push(p[0], p[1], p[2]); this.nrm.push(n[0], n[1], n[2]); this.col.push(c[0], c[1], c[2]);
      this.uv.push(U ? U[k][0] : 0, U ? U[k][1] : 0); this.tx.push(U ? 1 : 0);
    });
    for (let k = 1; k + 1 < P.length; k++) this.idx.push(base, base + k, base + k + 1);
  }
  // axis box; o.top (default true), o.bottom (false), o.cTop / o.cFront colours
  box(x0, x1, y0, y1, z0, z1, c, o = {}) {
    const ct = o.cTop || c, cf = o.cFront || c;
    if (o.top !== false) this.poly([[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]], [0, 1, 0], ct);
    if (o.bottom) this.poly([[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [0, -1, 0], c);
    this.poly([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [0, 0, 1], cf);
    this.poly([[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], [0, 0, -1], c);
    this.poly([[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [1, 0, 0], c);
    this.poly([[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], [-1, 0, 0], c);
  }
  // a square beam a → b (half width h), the 4 sides only
  beam(a, b, h, c) {
    const u = norm([b[0] - a[0], b[1] - a[1], b[2] - a[2]]);
    let p = cross(u, [0, 1, 0]);
    if (Math.hypot(...p) < 1e-6) p = [1, 0, 0];
    p = norm(p);
    const q = norm(cross(p, u));
    const S = [[p, q], [q, neg(p)], [neg(p), neg(q)], [neg(q), p]];   // (face normal, its side axis)
    for (const [n, s] of S) {
      const o = (pt, k) => [pt[0] + (n[0] + s[0] * k) * h, pt[1] + (n[1] + s[1] * k) * h, pt[2] + (n[2] + s[2] * k) * h];
      this.poly([o(a, -1), o(a, 1), o(b, 1), o(b, -1)], n, c);
    }
  }
  // a gable roof, ridge along x over z0..z1, eaves at yE, ridge at yR (6 tris)
  gable(x0, x1, z0, z1, yE, yR, c) {
    const zm = (z0 + z1) / 2;
    const nf = norm([0, z1 - zm, yR - yE]), nb = [0, nf[1], -nf[2]];
    this.poly([[x0, yE, z1], [x1, yE, z1], [x1, yR, zm], [x0, yR, zm]], nf, c);
    this.poly([[x1, yE, z0], [x0, yE, z0], [x0, yR, zm], [x1, yR, zm]], nb, c);
    this.poly([[x1, yE, z1], [x1, yE, z0], [x1, yR, zm]], [1, 0, 0], c);
    this.poly([[x0, yE, z0], [x0, yE, z1], [x0, yR, zm]], [-1, 0, 0], c);
  }
  // an n-gon annulus (outer, inner, top: 6n tris)
  ring(n, rO, rI, y0, y1, c) {
    for (let k = 0; k < n; k++) {
      const a0 = (k / n) * 2 * Math.PI, a1 = ((k + 1) / n) * 2 * Math.PI, am = (a0 + a1) / 2;
      const P = (r, a, y) => [Math.cos(a) * r, y, Math.sin(a) * r];
      const no = [Math.cos(am), 0, Math.sin(am)];
      this.poly([P(rO, a1, y0), P(rO, a0, y0), P(rO, a0, y1), P(rO, a1, y1)], no, c);
      this.poly([P(rI, a0, y0), P(rI, a1, y0), P(rI, a1, y1), P(rI, a0, y1)], neg(no), c);
      this.poly([P(rO, a0, y1), P(rI, a0, y1), P(rI, a1, y1), P(rO, a1, y1)], [0, 1, 0], c);
    }
  }
  disc(n, r, y, c) {
    const P = [];
    for (let k = n - 1; k >= 0; k--) { const a = (k / n) * 2 * Math.PI; P.push([Math.cos(a) * r, y, Math.sin(a) * r]); }
    this.poly(P, [0, 1, 0], c);
  }
  // a 4-sided pyramid cap over the rectangle at y0, apex at y1 (4 tris)
  cap(x0, x1, z0, z1, y0, y1, c) {
    const xm = (x0 + x1) / 2, zm = (z0 + z1) / 2, A = [xm, y1, zm], h = y1 - y0, dx = x1 - xm, dz = z1 - zm;
    this.poly([[x0, y0, z1], [x1, y0, z1], A], norm([0, dz, h]), c);
    this.poly([[x1, y0, z0], [x0, y0, z0], A], norm([0, dz, -h]), c);
    this.poly([[x1, y0, z1], [x1, y0, z0], A], norm([h, dx, 0]), c);
    this.poly([[x0, y0, z0], [x0, y0, z1], A], norm([-h, dx, 0]), c);
  }
  // an open trough L × W × H, walls t thick, water at wl (26 tris)
  trough(L, W, H, t, c, cw, wl) {
    const x0 = -L / 2, x1 = L / 2, z0 = -W / 2, z1 = W / 2, y0 = -FURN.sink_m;
    const i0 = x0 + t, i1 = x1 - t, j0 = z0 + t, j1 = z1 - t;
    this.poly([[x0, y0, z1], [x1, y0, z1], [x1, H, z1], [x0, H, z1]], [0, 0, 1], c);
    this.poly([[x1, y0, z0], [x0, y0, z0], [x0, H, z0], [x1, H, z0]], [0, 0, -1], c);
    this.poly([[x1, y0, z1], [x1, y0, z0], [x1, H, z0], [x1, H, z1]], [1, 0, 0], c);
    this.poly([[x0, y0, z0], [x0, y0, z1], [x0, H, z1], [x0, H, z0]], [-1, 0, 0], c);
    this.poly([[i0, wl, j1], [i1, wl, j1], [i1, H, j1], [i0, H, j1]], [0, 0, -1], c);
    this.poly([[i1, wl, j0], [i0, wl, j0], [i0, H, j0], [i1, H, j0]], [0, 0, 1], c);
    this.poly([[i1, wl, j1], [i1, wl, j0], [i1, H, j0], [i1, H, j1]], [-1, 0, 0], c);
    this.poly([[i0, wl, j0], [i0, wl, j1], [i0, H, j1], [i0, H, j0]], [1, 0, 0], c);
    this.poly([[x0, H, z1], [x1, H, z1], [i1, H, j1], [i0, H, j1]], [0, 1, 0], c);
    this.poly([[x1, H, z0], [x0, H, z0], [i0, H, j0], [i1, H, j0]], [0, 1, 0], c);
    this.poly([[x1, H, z1], [x1, H, z0], [i1, H, j0], [i1, H, j1]], [0, 1, 0], c);
    this.poly([[x0, H, z0], [x0, H, z1], [i0, H, j1], [i0, H, j0]], [0, 1, 0], c);
    this.poly([[i0, wl, j0], [i0, wl, j1], [i1, wl, j1], [i1, wl, j0]], [0, 1, 0], cw);
  }
  // a front-facing (+z) text quad at z, x0..x1 × y0..y1; s0..s1 = the slot's
  // horizontal share it shows (a square P shows the middle quarter)
  text(x0, x1, y0, y1, z, c, s0 = 0, s1 = 1) {
    this.poly([[x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z]], [0, 0, 1], c, [[s0, 0], [s1, 0], [s1, 1], [s0, 1]]);
  }
  // a text quad facing ±x (side = +1 / −1) at x, z0..z1 × y0..y1, read
  // unmirrored from that side (its right hand runs −z on +x, +z on −x)
  textX(x, z0, z1, y0, y1, side, c) {
    const P = side > 0 ? [[x, y0, z1], [x, y0, z0], [x, y1, z0], [x, y1, z1]] : [[x, y0, z0], [x, y0, z1], [x, y1, z1], [x, y1, z0]];
    this.poly(P, [side > 0 ? 1 : -1, 0, 0], c, [[0, 0], [1, 0], [1, 1], [0, 1]]);
  }
  // move the vertices from index v0 (a part built at the origin) by (dx, dy, dz)
  shift(v0, dx, dy, dz) {
    for (let i = v0 * 3; i < this.pos.length; i += 3) { this.pos[i] += dx; this.pos[i + 1] += dy; this.pos[i + 2] += dz; }
  }
  get verts() { return this.pos.length / 3; }
}
const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const neg = (v) => [-v[0], -v[1], -v[2]];

// ---------------------------------------------------------------- models
const S = FURN.sink_m;
// k = the look {L, D}: L the surface tone (seat, back panel), D the frame
function bench(m, k, { back = true, roof = false } = {}) {
  // two end frames, seat, backrest on two rear uprights (56 tris); covered:
  // the rear uprights run up to the roof (with or without the back panel)
  for (const x of [-0.7, 0.7]) m.box(x - 0.03, x + 0.03, -S, 0.42, -0.2, 0.2, k.D);
  m.box(-0.8, 0.8, 0.42, 0.47, -0.21, 0.21, k.L);
  if (back || roof) {
    for (const x of [-0.7, 0.7]) {
      if (roof) m.beam([x, 0.42, -0.24], [x, 2.15, -0.24], 0.035, k.D);
      else m.box(x - 0.03, x + 0.03, 0.42, 0.86, -0.25, -0.19, k.D, { top: false });
    }
  }
  if (back) m.box(-0.8, 0.8, 0.56, 0.86, -0.19, -0.15, k.L);
  if (roof) m.poly([[-1, 2.12, 0.45], [1, 2.12, 0.45], [1, 2.2, -0.45], [-1, 2.2, -0.45]], norm([0, 0.9, 0.08]), C.roof);
}
// DETAIL-1 (D1-FURN) street furniture parts
// the Swiss stop sign at the pole (xp, zp): the pole, the yellow name plate
// (the stop name on the atlas, on both faces) and the bus pictogram plate
// above it, PERPENDICULAR to the road (readable along it), reaching toward
// the road (+z) (40 tris)
function busSign(m, xp, zp) {
  m.box(xp - 0.03, xp + 0.03, -S, 2.75, zp - 0.03, zp + 0.03, C.steel);
  const h = 0.015, z0 = zp + 0.03, z1 = zp + 0.75, y0 = 2.0, y1 = 2.18;
  m.poly([[xp - h, y1, z0], [xp - h, y1, z1], [xp + h, y1, z1], [xp + h, y1, z0]], [0, 1, 0], C.yellow);
  m.poly([[xp - h, y0, z0], [xp + h, y0, z0], [xp + h, y0, z1], [xp - h, y0, z1]], [0, -1, 0], C.yellow);
  m.poly([[xp - h, y0, z1], [xp + h, y0, z1], [xp + h, y1, z1], [xp - h, y1, z1]], [0, 0, 1], C.yellow);
  m.poly([[xp + h, y0, z0], [xp - h, y0, z0], [xp - h, y1, z0], [xp + h, y1, z0]], [0, 0, -1], C.yellow);
  m.textX(xp + h, z0, z1, y0, y1, 1, C.yellow);
  m.textX(xp - h, z0, z1, y0, y1, -1, C.yellow);
  const q0 = zp + 0.03, q1 = zp + 0.45, p0 = 2.22, p1 = 2.64;
  m.box(xp - h, xp + h, p0, p1, q0, q1, C.yellow);
  for (const sd of [1, -1]) {   // the bus: a black body, a yellow window band (on both faces)
    const quad = (o, za, zb, ya, yb, c) => {
      const x = xp + sd * (h + o);
      m.poly(sd > 0 ? [[x, ya, zb], [x, ya, za], [x, yb, za], [x, yb, zb]] : [[x, ya, za], [x, ya, zb], [x, yb, zb], [x, yb, za]], [sd, 0, 0], c);
    };
    quad(0.003, q0 + 0.06, q1 - 0.06, p0 + 0.09, p1 - 0.1, C.ink);
    quad(0.006, q0 + 0.09, q1 - 0.09, p0 + 0.2, p1 - 0.15, C.yellow);
  }
}
// an open glass shelter behind the kerb (the stop node), its opening to the
// road (+z): four posts, a flat roof, the glass back + one side, a bench
// inside when the stop has one of its own (48 / 74 tris)
function busShelter(m, bench) {
  const x0 = -1.6, x1 = 1.6, zb = -1.55, zf = -0.15, H = 2.35;
  for (const x of [x0, x1]) for (const z of [zb, zf]) m.box(x - 0.04, x + 0.04, -S, H, z - 0.04, z + 0.04, C.frame, { top: false });
  m.box(x0 - 0.15, x1 + 0.15, H, H + 0.1, zb - 0.15, zf + 0.2, C.frame, { bottom: true });
  m.poly([[x1, 0.1, zb], [x0, 0.1, zb], [x0, 2.25, zb], [x1, 2.25, zb]], [0, 0, 1], C.glass);
  m.poly([[x0, 0.1, zb], [x0, 0.1, zf - 0.2], [x0, 2.25, zf - 0.2], [x0, 2.25, zb]], [1, 0, 0], C.glass);
  if (!bench) return;
  m.box(-1.2, 1.2, 0.42, 0.47, zb + 0.08, zb + 0.48, C.wood);
  for (const x of [-1.0, 1.0]) m.box(x - 0.03, x + 0.03, -S, 0.42, zb + 0.1, zb + 0.46, C.frame, { top: false });
}
// a flat bar a → b (xy, in the z = 0 plane), w wide (2 tris; the material is double-sided)
function bar(m, a, b, w, c) {
  const d = norm([b[0] - a[0], b[1] - a[1], 0]), o = [-d[1] * w / 2, d[0] * w / 2];
  m.poly([[a[0] - o[0], a[1] - o[1], 0], [b[0] - o[0], b[1] - o[1], 0], [b[0] + o[0], b[1] + o[1], 0], [a[0] + o[0], a[1] + o[1], 0]], [0, 0, 1], c);
}
// a gate leaf ±half wide between two posts: rails, end stiles, a brace (32 tris)
function gateLeaf(m, k, half, H, rails) {
  for (const x of [-half - 0.06, half + 0.06]) m.box(x - 0.06, x + 0.06, -S, H + 0.1, -0.06, 0.06, k.D);
  for (const y of rails) bar(m, [-half, y], [half, y], 0.06, k.L);
  for (const x of [-half + 0.03, half - 0.03]) bar(m, [x, rails[0] - 0.05], [x, rails[rails.length - 1] + 0.05], 0.06, k.L);
  bar(m, [-half + 0.05, rails[0]], [half - 0.05, rails[rails.length - 1]], 0.05, k.L);
}

// the model per variant key: (k = the look {L, D}, LOOK_DEF when untagged)
// → Mdl (+ text: null | 'name' | 'P' | 'WC', style)
const MODELS = {
  bench: (k) => { const m = new Mdl(); bench(m, k); return { m }; },
  bench_nb: (k) => { const m = new Mdl(); bench(m, k, { back: false }); return { m }; },
  bench_cov: (k) => { const m = new Mdl(); bench(m, k, { roof: true }); return { m }; },
  bench_cov_nb: (k) => { const m = new Mdl(); bench(m, k, { back: false, roof: true }); return { m }; },
  picnic_table: (k) => {
    const m = new Mdl();   // top, two trestles, two seats on two bearers (70)
    m.box(-0.9, 0.9, 0.72, 0.77, -0.38, 0.38, k.L);
    for (const x of [-0.72, 0.72]) {
      m.box(x - 0.03, x + 0.03, -S, 0.72, -0.28, 0.28, k.D);
      m.box(x - 0.04, x + 0.04, 0.36, 0.42, -0.72, 0.72, k.D);
    }
    for (const z of [-0.56, 0.56]) m.box(-0.9, 0.9, 0.42, 0.46, z - 0.14, z + 0.14, k.L);
    return { m };
  },
  fountain: (k) => {
    const m = new Mdl();   // stone trough, column with a cap, spout, jet (52)
    m.trough(1.6, 0.7, 0.62, 0.08, k.L, C.water, 0.48);
    m.box(0.86, 1.08, -S, 1.3, -0.11, 0.11, k.D, { top: false });
    m.cap(0.86, 1.08, -0.11, 0.11, 1.3, 1.45, k.D);
    m.box(0.5, 0.86, 0.98, 1.01, -0.015, 0.015, C.steel);
    m.poly([[0.5, 0.48, 0], [0.53, 0.48, 0], [0.53, 0.98, 0], [0.5, 0.98, 0]], [0, 0, 1], C.jet);
    m.poly([[0.515, 0.48, -0.015], [0.515, 0.48, 0.015], [0.515, 0.98, 0.015], [0.515, 0.98, -0.015]], [1, 0, 0], C.jet);
    return { m };
  },
  spring: (k) => {
    const m = new Mdl();   // a round stone basin, a stone block behind it with a pipe (74)
    m.ring(8, 0.55, 0.42, -S, 0.35, k.L);
    m.disc(8, 0.43, 0.25, C.water);
    m.box(-0.25, 0.25, -S, 0.75, -0.95, -0.55, k.D);
    m.box(-0.015, 0.015, 0.55, 0.58, -0.55, -0.25, C.steel);
    return { m };
  },
  firepit: (k) => {
    const m = new Mdl();   // a stone ring round the ash (54)
    m.ring(8, 0.7, 0.5, -S, 0.25, k.L);
    m.disc(8, 0.51, 0.05, C.ash);
    return { m };
  },
  bbq: (k) => {
    const m = new Mdl();   // a grill box on two leg frames, the grate on top (32)
    m.box(-0.4, 0.4, 0.62, 0.85, -0.25, 0.25, k.D);
    m.poly([[-0.36, 0.855, -0.21], [-0.36, 0.855, 0.21], [0.36, 0.855, 0.21], [0.36, 0.855, -0.21]], [0, 1, 0], C.grate);
    for (const x of [-0.34, 0.34]) m.box(x - 0.025, x + 0.025, -S, 0.62, -0.22, 0.22, k.D);
    return { m };
  },
  board: (k) => {
    const m = new Mdl();   // two posts, the panel, the name strip, the content, a small roof (46)
    for (const x of [-0.56, 0.56]) m.box(x - 0.04, x + 0.04, -S, 1.86, -0.04, 0.04, k.D);
    m.box(-0.62, 0.62, 1.0, 1.82, -0.03, 0.03, k.D);
    m.text(-0.56, 0.56, 1.5, 1.78, 0.032, C.board);
    m.poly([[-0.56, 1.04, 0.032], [0.56, 1.04, 0.032], [0.56, 1.46, 0.032], [-0.56, 1.46, 0.032]], [0, 0, 1], C.mapc);
    m.box(-0.72, 0.72, 1.86, 1.92, -0.12, 0.16, C.roof);
    return { m, text: 'name', style: 'board' };
  },
  map: (k) => {
    const m = new Mdl();   // a wider panel: the name strip over a map with two trail lines (50)
    for (const x of [-0.86, 0.86]) m.box(x - 0.045, x + 0.045, -S, 2.0, -0.045, 0.045, k.D);
    m.box(-0.92, 0.92, 0.9, 1.94, -0.03, 0.03, k.D);
    m.text(-0.8, 0.8, 1.5, 1.9, 0.032, C.board);
    m.poly([[-0.86, 0.95, 0.032], [0.86, 0.95, 0.032], [0.86, 1.46, 0.032], [-0.86, 1.46, 0.032]], [0, 0, 1], C.mapc);
    m.poly([[-0.7, 1.05, 0.036], [0.5, 1.33, 0.036], [0.5, 1.35, 0.036], [-0.7, 1.07, 0.036]], [0, 0, 1], C.red);
    m.poly([[-0.2, 0.98, 0.036], [0.7, 1.12, 0.036], [0.7, 1.14, 0.036], [-0.2, 1.0, 0.036]], [0, 0, 1], C.yellow);
    m.box(-1.02, 1.02, 2.0, 2.06, -0.14, 0.18, C.roof);
    return { m, text: 'name', style: 'board' };
  },
  route_marker: (k) => {
    const m = new Mdl();   // a post with a yellow plate (20)
    m.box(-0.04, 0.04, -S, 1.45, -0.04, 0.04, k.D);
    m.box(-0.12, 0.12, 1.15, 1.4, 0.04, 0.06, C.yellow);
    return { m };
  },
  cross_summit: (k) => {
    const m = new Mdl();   // a 3.6 m summit cross on a stone base (30)
    m.box(-0.35, 0.35, -S, 0.45, -0.35, 0.35, C.stoneD);
    m.box(-0.08, 0.08, 0.45, 3.6, -0.08, 0.08, k.D);
    m.box(-0.85, 0.85, 2.6, 2.76, -0.08, 0.08, k.D);
    return { m };
  },
  cross: (k) => {
    const m = new Mdl();   // a 1.9 m wayside cross under a little roof (26)
    m.box(-0.05, 0.05, -S, 1.9, -0.05, 0.05, k.D);
    m.box(-0.42, 0.42, 1.3, 1.4, -0.05, 0.05, k.D);
    m.gable(-0.16, 0.16, -0.16, 0.16, 1.88, 2.02, C.roof);
    return { m };
  },
  waste_basket: (k) => {
    const m = new Mdl();   // a post with a bin on its front (20)
    m.box(-0.03, 0.03, -S, 1.0, -0.03, 0.03, C.metal);
    m.box(-0.18, 0.18, 0.45, 0.95, 0.03, 0.31, k.L, { cTop: C.black });
    return { m };
  },
  toilets: (k) => {
    const m = new Mdl();   // a small hut: walls, gable roof, door, WC plate (18)
    m.box(-1.0, 1.0, -S, 2.3, -1.0, 1.0, k.L, { top: false });
    m.gable(-1.15, 1.15, -1.15, 1.15, 2.25, 2.95, C.roof);
    m.poly([[-0.4, 0, 1.01], [0.4, 0, 1.01], [0.4, 1.95, 1.01], [-0.4, 1.95, 1.01]], [0, 0, 1], C.woodD);
    m.text(-0.3, 0.3, 2.0, 2.2, 1.012, C.blue, 0.2, 0.8);
    return { m, text: 'WC', style: 'sign' };
  },
  playground: (k) => {
    const m = new Mdl();   // a swing: two A-frames, the top beam, two seats on ropes (70)
    for (const x of [-1.2, 1.2]) for (const z of [-0.8, 0.8]) m.beam([x, -S, z], [x, 2.2, 0], 0.045, k.L);
    m.box(-1.25, 1.25, 2.15, 2.25, -0.05, 0.05, k.L);
    for (const x of [-0.45, 0.45]) {
      m.box(x - 0.22, x + 0.22, 0.45, 0.49, -0.09, 0.09, C.black);
      for (const dx of [-0.2, 0.2]) m.poly([[x + dx - 0.01, 0.49, 0], [x + dx + 0.01, 0.49, 0], [x + dx + 0.01, 2.15, 0], [x + dx - 0.01, 2.15, 0]], [0, 0, 1], C.rope);
    }
    return { m };
  },
  charging_station: () => {
    const m = new Mdl();   // a charging column with a screen (12)
    m.box(-0.17, 0.17, -S, 1.5, -0.12, 0.12, C.white, { cTop: C.green });
    m.poly([[-0.11, 1.05, 0.125], [0.11, 1.05, 0.125], [0.11, 1.35, 0.125], [-0.11, 1.35, 0.125]], [0, 0, 1], C.screen);
    return { m };
  },
  charging_station_cov: () => {
    const m = MODELS.charging_station().m;   // + a canopy on two rear posts (30)
    for (const x of [-1.2, 1.2]) m.beam([x, -S, -0.6], [x, 2.6, -0.6], 0.05, C.steel);
    m.poly([[-1.4, 2.6, 1.2], [1.4, 2.6, 1.2], [1.4, 2.7, -0.8], [-1.4, 2.7, -0.8]], norm([0, 1, 0.05]), C.steel);
    return { m };
  },
  parking: () => {
    const m = new Mdl();   // a pole with the blue P sign (22)
    m.box(-0.03, 0.03, -S, 2.3, -0.03, 0.03, C.steel);
    m.box(-0.3, 0.3, 1.7, 2.3, 0.03, 0.06, C.blue);
    m.text(-0.27, 0.27, 1.73, 2.27, 0.062, C.blue, 0.375, 0.625);
    return { m, text: 'P', style: 'sign' };
  },
  bicycle_parking: (k) => {
    const m = new Mdl();   // a ground rail with five hoops (40)
    m.box(-1.1, 1.1, -S, 0.05, -0.03, 0.03, k.L);
    for (let j = -2; j <= 2; j++) {
      const x = j * 0.5;
      for (const z of [-0.32, 0.28]) m.poly([[x, 0, z], [x, 0, z + 0.04], [x, 0.78, z + 0.04], [x, 0.78, z]], [1, 0, 0], k.L);
      m.poly([[x, 0.74, -0.32], [x, 0.74, 0.32], [x, 0.78, 0.32], [x, 0.78, -0.32]], [1, 0, 0], k.L);
    }
    return { m };
  },
  bicycle_parking_cov: (k) => {
    const m = MODELS.bicycle_parking(k).m;   // + a lean-to roof on two rear posts (58)
    for (const x of [-1.2, 1.2]) m.beam([x, -S, -0.7], [x, 2.1, -0.7], 0.04, C.steel);
    m.poly([[-1.4, 1.9, 0.8], [1.4, 1.9, 0.8], [1.4, 2.1, -0.8], [-1.4, 2.1, -0.8]], norm([0, 1, 0.12]), C.steel);
    return { m };
  },
  shelter: (k) => {
    const m = new Mdl();   // an open hut: four posts, gable roof, back wall, a plank bench (50)
    for (const x of [-1.4, 1.4]) for (const z of [-1.1, 1.1]) m.beam([x, -S, z], [x, 2.3, z], 0.06, k.D);
    m.gable(-1.6, 1.6, -1.3, 1.3, 2.25, 3.0, C.roof);
    m.poly([[1.4, 0, -1.1], [-1.4, 0, -1.1], [-1.4, 1.3, -1.1], [1.4, 1.3, -1.1]], [0, 0, 1], k.L);
    m.box(-1.3, 1.3, -S, 0.45, -1.05, -0.7, k.L);
    return { m };
  },
  artwork: (k) => {
    const m = new Mdl();   // a plinth, a bronze form, the name plaque (20)
    m.box(-0.3, 0.3, -S, 0.9, -0.3, 0.3, C.concrete);
    const A = [0, 2.1, 0], B = [0, 0.9, 0], R = [[0.28, 1.5, 0], [0, 1.5, 0.2], [-0.28, 1.5, 0], [0, 1.5, -0.2]];
    for (let j = 0; j < 4; j++) {
      const p = R[j], q = R[(j + 1) % 4], d = norm([(p[0] + q[0]) / 2, 0, (p[2] + q[2]) / 2]);
      m.poly([p, q, A], norm([d[0], 0.5, d[2]]), k.L);
      m.poly([q, p, B], norm([d[0], -0.5, d[2]]), k.L);
    }
    m.text(-0.24, 0.24, 0.62, 0.74, 0.302, C.bronze);
    return { m, text: 'name', style: 'plaque' };
  },
  milestone: () => {
    const m = new Mdl();   // a stone with a hipped top, the name cut in the front (14)
    m.box(-0.16, 0.16, -S, 0.72, -0.11, 0.11, C.stone, { top: false });
    m.cap(-0.16, 0.16, -0.11, 0.11, 0.72, 0.86, C.stone);
    m.text(-0.15, 0.15, 0.5, 0.575, 0.112, C.stone);
    return { m, text: 'name', style: 'stone' };
  },
  plaque: () => {
    const m = new Mdl();   // a stone block with a bronze name plaque (12)
    m.box(-0.4, 0.4, -S, 1.0, -0.15, 0.15, C.stoneD);
    m.text(-0.3, 0.3, 0.62, 0.77, 0.152, C.bronze);
    return { m, text: 'name', style: 'plaque' };
  },
  survey_point: () => {
    const m = new Mdl();   // a small granite marker with a red top (10)
    m.box(-0.08, 0.08, -S, 0.3, -0.08, 0.08, C.stone, { cTop: C.red });
    return { m };
  },
  // DETAIL-1 (D1-FURN): bus stops — the node is the kerb, +z the road
  bus_stop: () => {
    const m = new Mdl();   // the stop sign alone (40)
    busSign(m, 0, 0);
    return { m, text: 'name', style: 'bus' };
  },
  bus_stop_bench: () => {
    const m = new Mdl();   // the sign + a bench of its own beside it, facing the road (96)
    busSign(m, 0, 0);
    const v0 = m.verts;
    bench(m, { L: C.wood, D: C.frame });
    m.shift(v0, -1.3, 0, -0.7);
    return { m, text: 'name', style: 'bus' };
  },
  bus_stop_shelter: () => {
    const m = new Mdl();   // a glass shelter + the sign at its end on the kerb (88)
    busShelter(m, false);
    busSign(m, 2.1, -0.15);
    return { m, text: 'name', style: 'bus' };
  },
  bus_stop_shelter_bench: () => {
    const m = new Mdl();   // ... with its bench inside (114)
    busShelter(m, true);
    busSign(m, 2.1, -0.15);
    return { m, text: 'name', style: 'bus' };
  },
  // DETAIL-1 point barriers, ACROSS the way (x across, centred on the node)
  lift_gate: () => {
    const m = new Mdl();   // the pivot housing + counterweight, a red-and-white boom (4.4 m), the rest post (58)
    const xa = -2.2, xb = 2.2, y0 = 0.92, y1 = 1.0, h = 0.04, e = h + 0.002;
    m.box(xa - 0.09, xa + 0.09, -S, 1.05, -0.09, 0.09, C.white);
    m.box(xa - 0.5, xa - 0.09, 0.9, 1.04, -h, h, C.metal);
    m.box(xa + 0.09, xb, y0, y1, -h, h, C.white, { bottom: true });
    for (let j = 0; j < 4; j++) {
      const a = xa + 0.5 + j * 1.0, b = a + 0.5;
      m.poly([[a, y0, e], [b, y0, e], [b, y1, e], [a, y1, e]], [0, 0, 1], C.red);
      m.poly([[b, y0, -e], [a, y0, -e], [a, y1, -e], [b, y1, -e]], [0, 0, -1], C.red);
    }
    m.box(xb - 0.03, xb + 0.03, -S, y0, -0.03, 0.03, C.white);
    return { m };
  },
  gate: (k) => {
    const m = new Mdl();   // a 3.2 m field gate: two posts, a leaf of three rails (32)
    gateLeaf(m, k, 1.6, 1.2, [0.3, 0.7, 1.1]);
    return { m };
  },
  gate_small: (k) => {
    const m = new Mdl();   // a 1.1 m pedestrian (kissing) gate (30)
    gateLeaf(m, k, 0.55, 1.1, [0.35, 1.0]);
    return { m };
  },
  stile: (k) => {
    const m = new Mdl();   // two posts with a top rail, a step on each side (50)
    for (const x of [-0.45, 0.45]) m.box(x - 0.05, x + 0.05, -S, 1.2, -0.05, 0.05, k.D);
    m.box(-0.5, 0.5, 0.95, 1.03, -0.04, 0.04, k.D);
    for (const z of [-1, 1]) m.box(-0.4, 0.4, 0.3, 0.36, z > 0 ? 0.1 : -0.4, z > 0 ? 0.4 : -0.1, k.L);
    return { m };
  },
  bollard: () => {
    const m = new Mdl();   // a 0.9 m grey post with a white reflective band (18)
    m.box(-0.07, 0.07, -S, 0.9, -0.07, 0.07, C.metal);
    const e = 0.072, ya = 0.7, yb = 0.78;
    m.poly([[-e, ya, e], [e, ya, e], [e, yb, e], [-e, yb, e]], [0, 0, 1], C.white);
    m.poly([[e, ya, -e], [-e, ya, -e], [-e, yb, -e], [e, yb, -e]], [0, 0, -1], C.white);
    m.poly([[e, ya, e], [e, ya, -e], [e, yb, -e], [e, yb, e]], [1, 0, 0], C.white);
    m.poly([[-e, ya, -e], [-e, ya, e], [-e, yb, e], [-e, yb, -e]], [-1, 0, 0], C.white);
    return { m };
  },
};
export const VARIANTS = Object.keys(MODELS);

// ---------------------------------------------------------------- look (FURN-T)
// a variant's two material tones when untagged (exactly the FURN-3D colours);
// a variant missing here takes no material / colour
const W = { L: C.wood, D: C.woodD }, ST = { L: C.stone, D: C.stoneD };
export const LOOK_DEF = {
  bench: W, bench_nb: W, bench_cov: W, bench_cov_nb: W, picnic_table: W, shelter: W,
  fountain: { L: C.concrete, D: C.concrete }, spring: ST, firepit: ST, bbq: { L: C.metal, D: C.metal },
  board: W, map: W, route_marker: W, cross: W, cross_summit: W,
  waste_basket: { L: C.bin, D: C.metal }, toilets: { L: C.wall, D: C.wall }, playground: { L: C.red, D: C.red },
  bicycle_parking: { L: C.steel, D: C.steel }, bicycle_parking_cov: { L: C.steel, D: C.steel },
  artwork: { L: C.bronze, D: C.bronze },
  gate: { L: C.steel, D: C.metal }, gate_small: { L: C.steel, D: C.metal }, stile: W,
};
// OSM material word (the bake's first / second ';' value) → a palette
export const MAT_WORD = {
  wood: 'wood', timber: 'wood', metal: 'metal', steel: 'metal', stainless_steel: 'metal', iron: 'metal',
  cast_iron: 'metal', aluminium: 'metal', aluminum: 'metal', stone: 'stone', granite: 'stone', sandstone: 'stone',
  limestone: 'stone', marble: 'stone', gneiss: 'stone', rock: 'stone', concrete: 'concrete',
  reinforced_concrete: 'concrete', plastic: 'plastic', recycled_plastic: 'plastic',
};
export const MATERIAL = {
  wood: W, metal: { L: C.steel, D: C.metal }, stone: ST, concrete: { L: C.concrete, D: C.concreteD },
  plastic: { L: C.plastic, D: C.plasticD },
};
// two materials split seat (L) / frame (D) only where the model has both parts
const SPLIT = new Set(['bench', 'bench_nb', 'bench_cov', 'bench_cov_nb', 'picnic_table']);
// models whose only material tone is the frame: the colour tag paints D
const COLOUR_D = new Set(['bbq', 'board', 'map', 'route_marker', 'cross', 'cross_summit']);
// W3C colour names (OSM colour=*) → sRGB hex; others are ignored
const COLOUR_NAME = {
  black: '000000', white: 'ffffff', gray: '808080', grey: '808080', silver: 'c0c0c0', darkgray: 'a9a9a9',
  darkgrey: 'a9a9a9', lightgray: 'd3d3d3', lightgrey: 'd3d3d3', dimgray: '696969', dimgrey: '696969',
  red: 'ff0000', darkred: '8b0000', maroon: '800000', brown: 'a52a2a', saddlebrown: '8b4513', sienna: 'a0522d',
  chocolate: 'd2691e', peru: 'cd853f', tan: 'd2b48c', burlywood: 'deb887', beige: 'f5f5dc', wheat: 'f5deb3',
  yellow: 'ffff00', gold: 'ffd700', orange: 'ffa500', green: '008000', darkgreen: '006400', forestgreen: '228b22',
  olive: '808000', olivedrab: '6b8e23', blue: '0000ff', navy: '000080', darkblue: '00008b', lightblue: 'add8e6',
  steelblue: '4682b4', purple: '800080', pink: 'ffc0cb', darkslategray: '2f4f4f', darkslategrey: '2f4f4f',
};
export const materialOf = (v) => (typeof v === 'string' && MAT_WORD[v.toLowerCase()]) || null;
// OSM colour → '#rrggbb' (lower-case) or null
export function colourOf(v) {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  return COLOUR_NAME[s] ? '#' + COLOUR_NAME[s] : null;
}
// the look of point p drawn as variant `key`: {id, L, D}, or null = the
// variant's own colours (untagged, unknown words, or a tag that equals them)
export function lookOf(p, key) {
  const def = LOOK_DEF[key], t = (p && p.t) || {};
  if (!def) return null;
  let L = def.L, D = def.D;
  const id = [];
  const m1 = materialOf(t.material), m2 = SPLIT.has(key) ? materialOf(t.material2) : null;
  if (m1 && m2 && m2 !== m1) {   // wood;concrete / concrete;wood → a wood seat on a concrete frame
    const seat = m2 === 'wood' ? m2 : m1, frame = seat === m1 ? m2 : m1;
    L = MATERIAL[seat].L; D = MATERIAL[frame].D; id.push(`${seat}/${frame}`);
  } else if (m1) { L = MATERIAL[m1].L; D = MATERIAL[m1].D; id.push(m1); }
  const c = colourOf(t.colour);
  if (c) { if (COLOUR_D.has(key)) D = lin(c); else L = lin(c); id.push(c); }
  if (!id.length || (L.every((v, i) => v === def.L[i]) && D.every((v, i) => v === def.D[i]))) return null;
  return { id: id.join('+'), L, D };
}

// the variant of an amenity point: a MODELS key or null (skipped / unknown).
// env.peakNear(x, z, r) → [{ele, dist}] (the gazetteer's peaks) or null
export function variantOf(p, env = {}) {
  const t = p.t || {};
  switch (p.k) {
    case 'bench': return t.covered ? (t.backrest === false ? 'bench_cov_nb' : 'bench_cov') : t.backrest === false ? 'bench_nb' : 'bench';
    case 'drinking_water': return 'fountain';
    case 'cross': return isSummit(p, env) ? 'cross_summit' : 'cross';
    case 'charging_station': return t.covered ? 'charging_station_cov' : 'charging_station';
    case 'bicycle_parking': return t.covered ? 'bicycle_parking_cov' : 'bicycle_parking';
    case 'historic': return t.historic === 'milestone' ? 'milestone' : 'plaque';
    case 'bus_stop': {   // a bench of its own only when tagged AND no mapped bench is there (bench_at)
      const b = t.bench === true && !t.bench_at;
      return t.shelter === true ? (b ? 'bus_stop_shelter_bench' : 'bus_stop_shelter') : b ? 'bus_stop_bench' : 'bus_stop';
    }
    case 'swing_gate': return 'gate';
    case 'kissing_gate': return 'gate_small';
    case 'barrier': return 'lift_gate';   // a swissTLM3D Durchfahrtssperre: kind unknown → the commonest mapped one
    default: return MODELS[p.k] ? p.k : null;
  }
}
// summit:cross=yes, or a gazetteer peak ≤ 80 m whose elevation is within 40 m
export function isSummit(p, env = {}) {
  if (p.t && p.t.summit_cross) return true;
  if (!env.peakNear) return false;
  const pk = env.peakNear(p.x, p.z, FURN.peak_r_m) || [];
  return pk.some((q) => !Number.isFinite(q.ele) || !Number.isFinite(p.y) || Math.abs(q.ele - p.y) <= FURN.peak_dz_m);
}

// a variant's template: flat typed arrays + its local bbox (cached). A
// look (lookOf) builds the variant in its tones under the key
// `variant|look.id`; such a key is only ever built with its look.
const TPL = new Map();
export const tplKey = (key, look) => (look ? `${key}|${look.id}` : key);
export function template(key, look = null) {
  const tk = tplKey(key, look);
  if (TPL.has(tk)) return TPL.get(tk);
  const f = MODELS[key];
  if (!f) return null;
  const { m, text = null, style = null } = f(look || LOOK_DEF[key] || {});
  const pos = Float32Array.from(m.pos);
  const bb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  for (let i = 0; i < pos.length; i += 3) {
    for (let a = 0; a < 3; a++) { bb[a] = Math.min(bb[a], pos[i + a]); bb[a + 3] = Math.max(bb[a + 3], pos[i + a]); }
  }
  const tpl = { key: tk, variant: key, look: look ? look.id : null, pos, nrm: Float32Array.from(m.nrm), col: Float32Array.from(m.col), uv: Float32Array.from(m.uv),
    tx: Uint8Array.from(m.tx), idx: Uint16Array.from(m.idx), verts: pos.length / 3, tris: m.tris, bbox: bb, text, style };
  TPL.set(tk, tpl);
  return tpl;
}

// ---------------------------------------------------------------- orientation
// a deterministic yaw from the OSM id (ids pass 2^32)
export function hashYaw(id) {
  const n = Math.abs(Number(id) || 0);
  let h = Math.imul((n % 4294967296) ^ Math.floor(n / 4294967296), 2654435761) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  return ((h ^ (h >>> 12)) >>> 0) / 4294967296 * 2 * Math.PI;
}
// the nearest point of a non-rail way of `graph` (RoadGraph) within r → {qx, qz, tx, tz, d} or null
export function nearestWay(graph, x, y, z, r = FURN.orient_r_m, dy = FURN.orient_dy_m) {
  if (!graph || !graph.near) return null;
  let best = null;
  graph.near(x, Number.isFinite(y) ? y : 0, z, r, Number.isFinite(y) ? dy : 1e9, (wi, pi) => {
    const w = graph.ways[wi];
    if (!w || w.kind === 'rail') return;
    const P = w.pts;
    for (const j of [pi - 1, pi]) {
      if (j < 0 || j + 1 >= P.length) continue;
      const ax = P[j][0], az = P[j][2], bx = P[j + 1][0], bz = P[j + 1][2];
      const dx = bx - ax, dz = bz - az, L2 = dx * dx + dz * dz;
      if (L2 < 1e-9) continue;
      const u = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2));
      const qx = ax + dx * u, qz = az + dz * u, d = Math.hypot(x - qx, z - qz);
      if (!best || d < best.d - 1e-9) { const l = Math.sqrt(L2); best = { qx, qz, tx: dx / l, tz: dz / l, d, tarmac: !!w.tarmac }; }
    }
  });
  return best;
}
// OSM direction (degrees clockwise from north) → the front (unit xz): x is
// east, z south (z = n1 − N), so north = −z
export function frontOfBearing(deg) {
  const a = (deg * Math.PI) / 180;
  return { fx: Math.sin(a), fz: -Math.cos(a) };
}
// the nearest water point (a lake ring edge or a river centreline) within r
// of (x, z) → {x, z, d} | null. lakes: [{poly: [[x, z]…], bbox}] (WaterIndex
// .lakes), rivers: [{pts: [[x, y, z]…]}] (buildRivers ways)
export function nearestWater(lakes, rivers, x, z, r) {
  let best = null;
  const seg = (ax, az, bx, bz) => {
    const dx = bx - ax, dz = bz - az, L2 = dx * dx + dz * dz;
    const u = L2 > 1e-12 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
    const qx = ax + dx * u, qz = az + dz * u, d = Math.hypot(x - qx, z - qz);
    if (d <= r && (!best || d < best.d)) best = { x: qx, z: qz, d };
  };
  for (const l of lakes || []) {
    const P = l.poly, b = l.bbox;
    if (!P || (b && (x < b[0] - r || x > b[2] + r || z < b[1] - r || z > b[3] + r))) continue;
    for (let i = 0; i < P.length; i++) { const a = P[i], c = P[(i + 1) % P.length]; seg(a[0], a[1], c[0], c[1]); }
  }
  for (const w of rivers || []) {
    const P = w.pts || [];
    for (let i = 0; i + 1 < P.length; i++) {
      const a = P[i], c = P[i + 1];
      if (Math.max(a[0], c[0]) < x - r || Math.min(a[0], c[0]) > x + r || Math.max(a[2], c[2]) < z - r || Math.min(a[2], c[2]) > z + r) continue;
      seg(a[0], a[2], c[0], c[2]);
    }
  }
  return best;
}
// a bench's front without OSM direction (FURN.bench): w = nearestWay | null,
// env.terrain(x, z) → y | null, env.water(x, z, r) → {x, z} | null
function benchFront(p, w, env) {
  const B = FURN.bench;
  // n: the perpendicular from the way to the bench (left of the tangent on the line)
  let nx = 0, nz = 0;
  if (w) {
    nx = -w.tz; nz = w.tx;
    if ((p.x - w.qx) * nx + (p.z - w.qz) * nz < 0) { nx = -nx; nz = -nz; }
    if (w.tarmac) return { fx: -nx, fz: -nz, how: 'road' };
  }
  const toward = (dx, dz, how) => {
    const L = Math.hypot(dx, dz);
    const ux = dx / L, uz = dz / L, c = ux * nx + uz * nz;
    if (w && Math.abs(c) >= B.cross_min) return { fx: Math.sign(c) * nx, fz: Math.sign(c) * nz, how };
    return { fx: ux, fz: uz, how };
  };
  const h = env && env.terrain;
  if (h) {
    const b = B.base_m;
    const e = h(p.x + b, p.z), wv = h(p.x - b, p.z), s = h(p.x, p.z + b), n = h(p.x, p.z - b);
    if ([e, wv, s, n].every(Number.isFinite)) {
      const dx = -(e - wv) / (2 * b), dz = -(s - n) / (2 * b);   // downhill
      if (Math.hypot(dx, dz) >= B.slope_min) return toward(dx, dz, 'downhill');
    }
  }
  const q = env && env.water ? env.water(p.x, p.z, B.water_r_m) : null;
  if (q && Math.hypot(q.x - p.x, q.z - p.z) > 1e-6) return toward(q.x - p.x, q.z - p.z, 'water');
  if (w) return { fx: -nx, fz: -nz, how: 'path' };
  return null;
}
// the front (unit xz) of point p: {fx, fz, how: 'osm' | 'road' | 'downhill' |
// 'water' | 'path' | 'id'}. The OSM direction leads: it is the bearing the
// feature faces = the model's front (+z): where a seated person looks, where
// a board's face points. A bench then follows benchFront (FURN.bench).
export function orientOf(p, graph, env = null) {
  const dir = p.t && p.t.direction;
  if (typeof dir === 'number' && Number.isFinite(dir)) return { ...frontOfBearing(dir), how: 'osm' };
  const free = () => { const a = hashYaw(p.id); return { fx: Math.sin(a), fz: Math.cos(a), how: 'id' }; };
  if (FREE.has(p.k) || (p.k === 'cross' && variantOf(p) === 'cross_summit')) return free();
  const w = nearestWay(graph, p.x, p.y, p.z);
  if (p.k === 'bench') { const b = benchFront(p, w, env); if (b) return b; }
  if (!w) return free();
  if (ACROSS.has(p.k)) return { fx: w.tx, fz: w.tz, how: 'path' };   // a barrier spans the way
  // the perpendicular pointing from the way to the object (left of the
  // tangent when the object sits on the line)
  let nx = -w.tz, nz = w.tx;
  if ((p.x - w.qx) * nx + (p.z - w.qz) * nz < 0) { nx = -nx; nz = -nz; }
  const s = FACE[p.k] || -1;
  return { fx: nx * s, fz: nz * s, how: 'path' };
}

// ---------------------------------------------------------------- text atlas
// pages (D1-FURN): a page is atlas_w × atlas_max_h (64 slots); `pages` stacks
// them in ONE texture (no second map, no new draw), slot s on page
// floor(s / perPage). Page 1 keeps slot 0 white + the first 63 texts exactly
// as before, so a world with ≤ 63 texts keeps its texture height and uvs.
export class TextAtlas {
  constructor(pages = 1) {
    this.cols = FURN.atlas_w / FURN.slot_w;
    this.perPage = this.cols * Math.floor(FURN.atlas_max_h / FURN.slot_h);
    this.pages = Math.max(1, Math.floor(pages));
    this.cap = this.perPage * this.pages;   // slot 0 = solid white
    this.keys = new Map();   // style \u0001 text -> slot
    this.refused = 0;
  }
  pageOf(slot) { return Math.floor(slot / this.perPage); }
  slot(style, text) {
    if (!style || !text) return 0;
    const k = style + '\u0001' + text;
    if (this.keys.has(k)) return this.keys.get(k);
    if (this.keys.size + 1 >= this.cap) { this.refused++; return 0; }
    this.keys.set(k, this.keys.size + 1);
    return this.keys.size;
  }
  height() { return Math.max(1, Math.ceil((this.keys.size + 1) / this.cols)) * FURN.slot_h; }
  // [u0, v0 (bottom), u1, v1 (top)] of a slot, 1.5 texels inset (flipY texture)
  rect(slot, H) {
    const c = slot % this.cols, r = Math.floor(slot / this.cols), W = FURN.atlas_w, e = 1.5;
    return [(c * FURN.slot_w + e) / W, 1 - ((r + 1) * FURN.slot_h - e) / H, ((c + 1) * FURN.slot_w - e) / W, 1 - (r * FURN.slot_h + e) / H];
  }
  // browser only: the canvas texture. paintSteps = THE paint, a step per
  // `every` texts on the one canvas (R8 M6b: the whole atlas was one 60-67 ms
  // task in the Map dwell); paint = its drain
  paint(H) {
    const it = this.paintSteps(H);
    for (;;) { const r = it.next(); if (r.done) return r.value; }
  }
  *paintSteps(H, every = 8) {
    const cv = document.createElement('canvas');
    cv.width = FURN.atlas_w; cv.height = H;
    const g = cv.getContext('2d');
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, FURN.slot_w, FURN.slot_h);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    let n = 0;
    for (const [k, slot] of this.keys) {
      if (++n % every === 0) yield;
      const [style, text] = k.split('\u0001');
      const st = STYLE[style] || STYLE.board;
      const x = (slot % this.cols) * FURN.slot_w, y = Math.floor(slot / this.cols) * FURN.slot_h;
      g.fillStyle = st.bg; g.fillRect(x, y, FURN.slot_w, FURN.slot_h);
      let px = st.px, t = text;
      const font = () => `${st.bold ? 'bold ' : ''}${px}px "Helvetica Neue", Arial, sans-serif`;
      g.font = font();
      while (px > 16 && g.measureText(t).width > FURN.slot_w - 16) { px -= 2; g.font = font(); }
      while (t.length > 3 && g.measureText(t).width > FURN.slot_w - 16) t = t.slice(0, -2) + '…';
      g.fillStyle = st.fg;
      g.fillText(t, x + FURN.slot_w / 2, y + FURN.slot_h / 2 + 2);
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
  }
}

// the atlas material: the trail signposts' program (no new shader) — another
// TextAtlas user (D1-BLD shop signs) builds its mesh with this, never its own params
export function atlasMaterial(tex) {
  return new THREE.MeshBasicMaterial({ vertexColors: true, map: tex, side: THREE.DoubleSide, fog: true });
}

// ---------------------------------------------------------------- plan
// points (the MAP-D amenity list) → the placed set: typed arrays indexed by
// item, `ai` = the index into points (the inspect card's key); keys[ti] =
// the template key (variant, or `variant|look` for a tagged look). env:
// {graph, peakNear}. Deterministic: the same input gives the same plan.
// planFurnitureSteps = THE plan, a step per `every` points (M7c: a point's
// orientOf — nearestWay, a bench's nearestWater — made the whole plan one
// 120-130 ms task after the Beam); the plan is built aside and returned at
// the end; planFurniture = its drain
export function planFurniture(points, env = {}) {
  const it = planFurnitureSteps(points, env);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* planFurnitureSteps(points, env = {}, every = 8) {
  const st = { points: 0, items: 0, near: 0, far: 0, skipped: {}, unknown: {}, by_variant: {}, by_look: {},
    oriented: { osm: 0, road: 0, downhill: 0, water: 0, path: 0, id: 0 }, texts: 0, texts_refused: 0, tris_all: 0 };
  const keys = [], tplOf = new Map();
  const rows = [];
  const atlas = new TextAtlas(FURN.atlas_pages);
  const list = Array.isArray(points) ? points : [];
  st.points = list.length;
  // the fixed signs first, then the names in a stable order (id)
  const named = [];
  for (let ai = 0; ai < list.length; ai++) {
    if (ai && ai % every === 0) yield;
    const p = list[ai];
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.z)) continue;
    if (SKIP_KINDS.has(p.k)) { st.skipped[p.k] = (st.skipped[p.k] || 0) + 1; continue; }
    const key = variantOf(p, env);
    if (!key) { st.unknown[p.k] = (st.unknown[p.k] || 0) + 1; continue; }
    const look = lookOf(p, key), tk = tplKey(key, look);
    const tpl = template(key, look);
    if (!tplOf.has(tk)) { tplOf.set(tk, keys.length); keys.push(tk); }
    if (look) st.by_look[look.id] = (st.by_look[look.id] || 0) + 1;
    const o = orientOf(key === 'cross_summit' ? { ...p, t: { ...(p.t || {}), summit_cross: true } } : p, env.graph, env);
    st.oriented[o.how]++;
    const g = gateOf(p) === 'near' ? 0 : 1;
    st[g ? 'far' : 'near']++;
    st.by_variant[key] = (st.by_variant[key] || 0) + 1;
    st.tris_all += tpl.tris;
    const row = { ai, ti: tplOf.get(tk), x: p.x, z: p.z, y: Number.isFinite(p.y) ? p.y : NaN, fx: o.fx, fz: o.fz, g, slot: 0 };
    rows.push(row);
    if (tpl.text === 'name' && p.name) named.push({ row, style: tpl.style, text: String(p.name), id: p.id });
    else if (tpl.text && tpl.text !== 'name') row.slot = atlas.slot(tpl.style, tpl.text);
  }
  const KO = { board: 0, map: 0, plaque: 1, stone: 1 };
  named.sort((a, b) => (KO[a.style] ?? 2) - (KO[b.style] ?? 2) || (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
  for (const n of named) n.row.slot = atlas.slot(n.style, n.text);
  st.texts = atlas.keys.size; st.texts_refused = atlas.refused;
  const n = rows.length;
  const plan = {
    n, keys, tpls: keys.map((k) => TPL.get(k)), atlas,
    ai: new Int32Array(n), ti: new Uint16Array(n), x: new Float32Array(n), z: new Float32Array(n), y: new Float32Array(n),
    ys: new Float32Array(n).fill(NaN), fx: new Float32Array(n), fz: new Float32Array(n), g: new Uint8Array(n),
    slot: new Int16Array(n), stats: st,
  };
  rows.forEach((r, i) => {
    plan.ai[i] = r.ai; plan.ti[i] = r.ti; plan.x[i] = r.x; plan.z[i] = r.z; plan.y[i] = r.y;
    plan.fx[i] = r.fx; plan.fz[i] = r.fz; plan.g[i] = r.g; plan.slot[i] = r.slot;
  });
  st.items = n;
  return plan;
}

// ---------------------------------------------------------------- grid
export class FurnGrid {
  constructor(plan, cell = FURN.cell_m) {
    this.plan = plan; this.cell = cell; this.cells = new Map();
    for (let i = 0; i < plan.n; i++) {
      const k = this.key(Math.floor(plan.x[i] / cell), Math.floor(plan.z[i] / cell));
      let a = this.cells.get(k);
      if (!a) this.cells.set(k, a = []);
      a.push(i);
    }
  }
  key(cx, cz) { return (cx + 4096) * 16384 + (cz + 4096); }
  // the items to draw from (px, pz): near ≤ near_m, far ≤ far_m → count written into out (Int32Array)
  visible(px, pz, out, nearM = FURN.near_m, farM = FURN.far_m) {
    const P = this.plan, c = this.cell, n2 = nearM * nearM, f2 = farM * farM;
    let n = 0;
    const cx0 = Math.floor((px - farM) / c), cx1 = Math.floor((px + farM) / c);
    const cz0 = Math.floor((pz - farM) / c), cz1 = Math.floor((pz + farM) / c);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const a = this.cells.get(this.key(cx, cz));
        if (!a) continue;
        for (const i of a) {
          const dx = P.x[i] - px, dz = P.z[i] - pz, d2 = dx * dx + dz * dz;
          if (d2 <= (P.g[i] ? f2 : n2) && n < out.length) out[n++] = i;
        }
      }
    }
    return n;
  }
}
// moved far enough since the last build (or never built)?
export function needsRebuild(last, x, y, z, step = FURN.step_m) {
  if (!last) return true;
  const dx = x - last.x, dy = y - last.y, dz = z - last.z;
  return dx * dx + dy * dy + dz * dz > step * step;
}

// the Player's ground query as furniture's stand (main.js; the gate wires a
// real Player the same way): skin = the drawn terrain point (standHeight),
// ground = Player._groundY WITHOUT the lake float (an object never swims),
// ceiling = Player.headroomAt (the spans roof / the lowest structure sheet
// over the seat itself — a girder in the body's step band counts: an object
// cannot step over it). ready() false (spans / structures still loading) → every answer
// null: the snap stays pending, never caches a terrain-only seat.
export function standOf(player, ready = () => true) {
  return {
    skin: (x, z) => (ready() ? player.hf.standHeight(x, z) : null),
    ground: (x, y, z) => (ready() ? player._groundY(x, y, z, false) : null),
    ceiling: (x, y, z) => player.headroomAt(x, y, z),
  };
}

// the seat of an object h tall at (x, z) whose data height is y0 (the bake's
// terrain there) → {y, how: 'skin' | 'floor'} or null (not resident). The
// levels are the Player's (stand.ground at a probe height); a level counts
// only with room for the item under the sheet over it (stand.ceiling):
//   a = what a body at the data height stands on (skin / spans bed / a
//       structure floor within the step);
//   b = the floor a body FURN.deck_reach_m higher stands on — wins when it
//       is nearer the data height than a (an open cut's bed vs the deck
//       over it at grade);
//   neither has room (the item would stand inside a low deck) → the floor
//       up to deck_depth_m over that sheet's underside: the deck top — only
//       when it is ≤ climbMax (deck_reach_m + deck_depth_m) over the data
//       height: a higher deck is another level, the item stays on a (a bus
//       stop taller than a footbridge's clearance stays under it);
//   else a — a bench under a bridge with headroom stays under it.
export function seatOf(x, z, y0, h, stand, climbMax = FURN.deck_reach_m + FURN.deck_depth_m) {
  const sk = stand.skin(x, z);
  if (sk === null || sk === undefined || !Number.isFinite(sk)) return null;
  const y = Number.isFinite(y0) ? y0 : sk;
  const ok = (v) => v !== null && v !== undefined && Number.isFinite(v);
  const a = stand.ground(x, y, z);
  if (!ok(a)) return null;
  const room = (g) => { const c = stand.ceiling(x, g, z); return !ok(c) || c - g >= h; };
  let best = room(a) ? a : null;
  const b = stand.ground(x, y + FURN.deck_reach_m, z);
  if (ok(b) && b > a + 1e-3 && room(b) && (best === null || Math.abs(b - y) < Math.abs(best - y))) best = b;
  if (best === null) {
    const c = stand.ceiling(x, a, z);
    const t = ok(c) ? stand.ground(x, c + FURN.deck_depth_m, z) : null;
    if (ok(t) && t > a + 1e-3 && t - y <= climbMax && room(t)) best = t;
  }
  if (best === null) best = a;
  return { y: best, how: Math.abs(best - sk) <= 1e-3 ? 'skin' : 'floor' };
}

// the ground under item i (cached once it answers), else the data y. With a
// stand (main.js): seatOf at the centre; a raised level (deck, structure
// floor, spans bed) is the seat itself, the terrain level takes the min of
// stand.skin over the centre + the footprint corners (the pre-stand rule,
// byte-identical on plain terrain). Furniture always hands a stand; a bare
// `ground(x, z)` without one (the gates' fixtures) takes the same min over
// the centre + corners.
export function groundOf(plan, i, ground, stand = null) {
  if (Number.isFinite(plan.ys[i])) return { y: plan.ys[i], snapped: true };
  if (stand) {
    const s = seatOf(plan.x[i], plan.z[i], plan.y[i], plan.tpls[plan.ti[i]].bbox[4], stand);
    if (!s) return { y: plan.y[i], snapped: false };
    if (s.how === 'floor') { plan.ys[i] = s.y; return { y: s.y, snapped: true }; }
    ground = stand.skin;
  }
  if (ground) {
    const T = plan.tpls[plan.ti[i]], b = T.bbox, fx = plan.fx[i], fz = plan.fz[i];
    let m = Infinity, ok = true;
    for (const [lx, lz] of [[0, 0], [b[0], b[2]], [b[3], b[2]], [b[3], b[5]], [b[0], b[5]]]) {
      const h = ground(plan.x[i] + lx * fz + lz * fx, plan.z[i] - lx * fx + lz * fz);
      if (h === null || h === undefined || !Number.isFinite(h)) { ok = false; break; }
      m = Math.min(m, h);
    }
    if (ok) { plan.ys[i] = m; return { y: m, snapped: true }; }
  }
  return { y: plan.y[i], snapped: false };
}

// ---------------------------------------------------------------- the optic
// the optic cull (furniture-limits): a gate's naked distance × the view's
// magnification over the naked eye — main.js's `mag`, the one the far window
// takes: the desktop / phone view Z, an optic's zoom × detail_mag_1x — never
// under the naked gate, capped at maxM. At mag ≤ 1 it IS the naked gate.
export function opticRange(gateM, mag, maxM = FURN.optic_max_m) {
  if (!(mag > 1)) return gateM;
  return Math.max(gateM, Math.min(maxM, gateM * mag));
}
// mag on the optic_step grid at or below it (a re-plan only per notch)
export function magStep(mag, step = FURN.optic_step) {
  if (!(mag > 1)) return 1;
  return Math.pow(step, Math.floor(Math.log(mag) / Math.log(step) + 1e-6));
}

// the optic set: every item NOT in the naked set (naked[i] = 1) whose
// position the wedge hits (focus.hit from the apex) within its gate's
// opticRange, nearest the apex first, admitted while the running tris stay
// ≤ budget → {n (written into out), tris, want, dropped}. focus inactive →
// nothing.
export function opticVisible(plan, focus, mag, naked, out, budget = FURN.optic_tri_budget,
  nearM = FURN.near_m, farM = FURN.far_m) {
  const r = { n: 0, tris: 0, want: 0, dropped: 0 };
  if (!plan || !focus || !focus.active || !(mag > 1)) return r;
  const rN = opticRange(nearM, mag), rF = opticRange(farM, mag);
  const cand = [];
  for (let i = 0; i < plan.n; i++) {
    if (naked && naked[i]) continue;
    const g = plan.g[i] ? rF : rN;
    if (!focus.hit(plan.x[i], plan.z[i], 0, g)) continue;
    cand.push([Math.hypot(plan.x[i] - focus.ox, plan.z[i] - focus.oz), i]);
  }
  cand.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  r.want = cand.length;
  for (const [, i] of cand) {
    const t = plan.tpls[plan.ti[i]].tris;
    if (r.tris + t > budget || r.n >= out.length) { r.dropped++; continue; }
    out[r.n++] = i; r.tris += t;
  }
  return r;
}

// count the vertices / indices of a visible set
export function sizeOf(plan, vis, n) {
  let v = 0, t = 0;
  for (let k = 0; k < n; k++) { const T = plan.tpls[plan.ti[vis[k]]]; v += T.verts; t += T.tris; }
  return { verts: v, tris: t };
}

// write the visible set into the buffers (pos 3, col 3, uv 2, idx); yb[k] =
// the base y used per visible item (pick reads it); stand: groundOf's. →
// {verts, tris, pending}
export function writeVisible(plan, vis, n, buf, ground = null, H = FURN.slot_h, pend = null, stand = null) {
  const { pos, col, uv, idx, yb } = buf;
  const A = plan.atlas, white = A.rect(0, H), wu = (white[0] + white[2]) / 2, wv = (white[1] + white[3]) / 2;
  let v = 0, t = 0, pending = 0;
  for (let k = 0; k < n; k++) {
    const i = vis[k], T = plan.tpls[plan.ti[i]];
    const fx = plan.fx[i], fz = plan.fz[i], X = plan.x[i], Z = plan.z[i];
    const gy = groundOf(plan, i, ground, stand);
    if (!gy.snapped && pend && pend(i)) pending++;
    const Y = Number.isFinite(gy.y) ? gy.y : 0;
    yb[k] = Y;
    const R = plan.slot[i] > 0 ? A.rect(plan.slot[i], H) : null;
    for (let j = 0; j < T.verts; j++) {
      const lx = T.pos[j * 3], ly = T.pos[j * 3 + 1], lz = T.pos[j * 3 + 2];
      const o = (v + j) * 3;
      pos[o] = X + lx * fz + lz * fx; pos[o + 1] = Y + ly; pos[o + 2] = Z - lx * fx + lz * fz;
      const nx = T.nrm[j * 3], ny = T.nrm[j * 3 + 1], nz = T.nrm[j * 3 + 2];
      const s = shadeOf(nx * fz + nz * fx, ny, -nx * fx + nz * fz);
      const txt = T.tx[j] && R;
      col[o] = (txt ? 1 : T.col[j * 3]) * s; col[o + 1] = (txt ? 1 : T.col[j * 3 + 1]) * s; col[o + 2] = (txt ? 1 : T.col[j * 3 + 2]) * s;
      const u = (v + j) * 2;
      if (txt) { uv[u] = R[0] + T.uv[j * 2] * (R[2] - R[0]); uv[u + 1] = R[1] + T.uv[j * 2 + 1] * (R[3] - R[1]); }
      else { uv[u] = wu; uv[u + 1] = wv; }
    }
    for (let j = 0; j < T.idx.length; j++) idx[t * 3 + j] = v + T.idx[j];
    v += T.verts; t += T.tris;
  }
  return { verts: v, tris: t, pending };
}

// ray (ox..dz, unit dir) vs item i's oriented box (base y yb) → t or null
export function rayItem(plan, i, yb, r, pad = FURN.pick_pad_m) {
  const T = plan.tpls[plan.ti[i]], b = T.bbox, fx = plan.fx[i], fz = plan.fz[i];
  // into the local frame: x = R (fz, −fx), z = F (fx, fz)
  const ox = r.ox - plan.x[i], oz = r.oz - plan.z[i];
  const o = [ox * fz - oz * fx, r.oy - yb, ox * fx + oz * fz];
  const d = [r.dx * fz - r.dz * fx, r.dy, r.dx * fx + r.dz * fz];
  let t0 = 0, t1 = Infinity;
  for (let a = 0; a < 3; a++) {
    const lo = b[a] - pad, hi = b[a + 3] + pad;
    if (Math.abs(d[a]) < 1e-9) { if (o[a] < lo || o[a] > hi) return null; continue; }
    let ta = (lo - o[a]) / d[a], tb = (hi - o[a]) / d[a];
    if (ta > tb) { const s = ta; ta = tb; tb = s; }
    t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }
  return t0;
}

// ---------------------------------------------------------------- the draw
export class Furniture {
  // opts: {stand (standOf(player, ready): the Player's ground query — the
  // seat), peakNear(x, z, r), terrain(x, z) → y | null and water(x, z, r)
  // → {x, z} | null (a bench's front)}
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.stand = opts.stand || null;
    this.peakNear = opts.peakNear || null;
    this.terrain = opts.terrain || null;
    this.water = opts.water || null;
    this.plan = null; this.grid = null; this.mesh = null;
    this.vis = new Int32Array(0); this.yb = new Float32Array(0); this.nVis = 0;
    this._last = null; this._at = -Infinity; this._pending = 0;
    // the optic layer (opticVisible): its own mesh on the focus layer, the
    // naked set as a per-item mark, the key it was planned for
    this.optic = null; this.oVis = new Int32Array(0); this.naked = new Uint8Array(0); this.nOptic = 0;
    this._oKey = ''; this._nakedGen = 0; this.opticDrawn = null;
    this.stats = null;
  }

  // points: the MAP-D amenity list (null / empty → nothing); graph: RoadGraph.
  // buildSteps = THE build: the plan in steps (planFurnitureSteps), then the
  // atlas paint in steps (paintSteps), the mesh at its end (main.js publishes the Furniture once
  // it is through); build = its drain
  build(points, graph) {
    const it = this.buildSteps(points, graph);
    for (;;) { const r = it.next(); if (r.done) return r.value; }
  }
  *buildSteps(points, graph) {
    this.dispose();
    this.plan = yield* planFurnitureSteps(points, { graph, peakNear: this.peakNear, terrain: this.terrain, water: this.water });
    yield;
    const st = this.plan.stats;
    if (!this.plan.n) { this.stats = st; return st; }
    this.grid = new FurnGrid(this.plan);
    this.vis = new Int32Array(this.plan.n); this.yb = new Float32Array(this.plan.n);
    this.oVis = new Int32Array(this.plan.n); this.naked = new Uint8Array(this.plan.n);
    this.H = this.plan.atlas.height();
    const tex = typeof document !== 'undefined' ? yield* this.plan.atlas.paintSteps(this.H) : null;
    const geo = new THREE.BufferGeometry();
    this._alloc(geo, 1024);
    this.mesh = new THREE.Mesh(geo, atlasMaterial(tex));
    this.mesh.name = 'trail-furniture';
    this.mesh.frustumCulled = false;   // a ≤ 600 m ring around the player: always in view
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrixWorld(true);
    geo.setDrawRange(0, 0);
    if (this.scene) this.scene.add(this.mesh);
    Object.assign(st, { atlas_texels: `${FURN.atlas_w}x${this.H}`, draws: 1 });
    this.stats = st;
    return st;
  }

  // drop the mesh, its buffers and its atlas (a second build starts clean)
  dispose() {
    if (this.optic) {
      if (this.optic.parent) this.optic.parent.remove(this.optic);
      this.optic.geometry.dispose();             // the material is the naked mesh's
    }
    if (this.mesh) {
      if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
      this.mesh.geometry.dispose();
      if (this.mesh.material.map) this.mesh.material.map.dispose();
      this.mesh.material.dispose();
    }
    this.mesh = null; this.grid = null; this.nVis = 0; this._last = null; this._pending = 0;
    this.optic = null; this.nOptic = 0; this._oKey = ''; this.opticDrawn = null;
  }

  _alloc(geo, verts, tris = verts) {
    const A = (n, k) => { const a = new THREE.BufferAttribute(new Float32Array(n * k), k); a.setUsage(THREE.DynamicDrawUsage); return a; };
    geo.setAttribute('position', A(verts, 3));
    geo.setAttribute('color', A(verts, 3));
    geo.setAttribute('uv', A(verts, 2));
    const ix = new THREE.BufferAttribute(new Uint32Array(tris * 3), 1);
    ix.setUsage(THREE.DynamicDrawUsage);
    geo.setIndex(ix);
    geo.userData.capV = verts; geo.userData.capT = tris;
    if (geo === (this.mesh && this.mesh.geometry) || !this.mesh) { this._capV = verts; this._capT = tris; }
  }

  // per frame: rebuild the drawn set when the player moved > step_m, or a
  // height snap is pending (≥ resnap_ms apart); then the optic layer
  // (focus: the Focus wedge, mag: the view's magnification — main.js)
  update(pos, now = 0, force = false, focus = null, mag = 1) {
    if (!this.mesh || !pos) return false;
    const r = this._updateNaked(pos, now, force);
    if (focus) this._updateOptic(focus, mag);
    return r;
  }

  _updateNaked(pos, now, force) {
    const due = force || needsRebuild(this._last, pos.x, pos.y, pos.z)
      || (this._pending > 0 && now - this._at >= FURN.resnap_ms);
    if (!due) return false;
    this._last = { x: pos.x, y: pos.y, z: pos.z };
    this._at = now;
    const P = this.plan;
    const n = this.grid.visible(pos.x, pos.z, this.vis);
    const sz = sizeOf(P, this.vis, n);
    const geo = this.mesh.geometry;
    if (sz.verts > this._capV || sz.tris > this._capT) {
      geo.dispose();
      this._alloc(geo, Math.ceil(Math.max(sz.verts, this._capV) * 1.5), Math.ceil(Math.max(sz.tris, this._capT) * 1.5));
    }
    const a = geo.attributes, r2 = FURN.snap_r_m * FURN.snap_r_m;
    const near = (i) => { const dx = P.x[i] - pos.x, dz = P.z[i] - pos.z; return dx * dx + dz * dz <= r2; };
    const w = writeVisible(P, this.vis, n, { pos: a.position.array, col: a.color.array, uv: a.uv.array, idx: geo.index.array, yb: this.yb },
      null, this.H, near, this.stand);
    this.nVis = n; this._pending = w.pending;
    Furniture._commit(geo, w);
    this.drawn = { items: n, tris: w.tris, verts: w.verts };
    this.naked.fill(0);
    for (let k = 0; k < n; k++) this.naked[this.vis[k]] = 1;
    this._nakedGen++;
    return true;
  }

  static _commit(geo, w) {
    const a = geo.attributes;
    for (const [att, k] of [[a.position, 3], [a.color, 3], [a.uv, 2]]) {
      att.clearUpdateRanges(); att.addUpdateRange(0, w.verts * k); att.needsUpdate = true;
    }
    geo.index.clearUpdateRanges(); geo.index.addUpdateRange(0, w.tris * 3); geo.index.needsUpdate = true;
    geo.setDrawRange(0, w.tris * 3);
  }

  // the optic layer: re-planned only when the wedge (gen), the magnification
  // notch, the naked set or the active flag moved. Items keep their cached
  // seat, else the data y (no snap past the near ring); not pickable.
  _updateOptic(focus, mag) {
    const on = !!focus.active && mag > 1;
    const key = on ? `${focus.gen}|${magStep(mag)}|${this._nakedGen}` : 'off';
    if (key === this._oKey) return;
    this._oKey = key;
    if (!on) {
      if (this.optic) { this.optic.geometry.setDrawRange(0, 0); this.optic.visible = false; }
      this.nOptic = 0; this.opticDrawn = null;
      return;
    }
    if (!this.optic) {
      const geo = new THREE.BufferGeometry();
      this._alloc(geo, 1024);
      this.optic = new THREE.Mesh(geo, this.mesh.material);
      this.optic.name = 'trail-furniture-optic';
      this.optic.frustumCulled = false;   // the wedge's set: in the optic's view by construction
      this.optic.matrixAutoUpdate = false;
      this.optic.updateMatrixWorld(true);
      this.optic.layers.set(focus.layer);   // optic passes only: the naked pass never sees it
      if (this.scene) this.scene.add(this.optic);
    }
    const P = this.plan, geo = this.optic.geometry;
    const o = opticVisible(P, focus, magStep(mag), this.naked, this.oVis);
    const sz = sizeOf(P, this.oVis, o.n);
    if (sz.verts > geo.userData.capV || sz.tris > geo.userData.capT) {
      geo.dispose();
      this._alloc(geo, Math.ceil(Math.max(sz.verts, geo.userData.capV) * 1.5), Math.ceil(Math.max(sz.tris, geo.userData.capT) * 1.5));
    }
    const a = geo.attributes;
    if (this._oyb === undefined || this._oyb.length < P.n) this._oyb = new Float32Array(P.n);
    const w = writeVisible(P, this.oVis, o.n, { pos: a.position.array, col: a.color.array, uv: a.uv.array, idx: geo.index.array, yb: this._oyb },
      null, this.H);
    Furniture._commit(geo, w);
    this.optic.visible = o.n > 0;
    this.nOptic = o.n;
    this.opticDrawn = { items: o.n, tris: w.tris, verts: w.verts, want: o.want, dropped: o.dropped };
  }

  // the inspect hit: ray {ox, oy, oz, dx, dy, dz} (unit dir) vs the drawn
  // boxes, nearest t ≤ maxT + 0.75 (nothing solid in front of it); else the
  // aimed point P within near_pt_m of one → {i (amenity index), t, y, how}
  pick(ray, P = null, maxT = Infinity) {
    if (!this.plan || !this.nVis) return null;
    const pl = this.plan;
    let best = null;
    if (ray) {
      for (let k = 0; k < this.nVis; k++) {
        const t = rayItem(pl, this.vis[k], this.yb[k], ray);
        if (t !== null && t <= maxT + 0.75 && (!best || t < best.t)) best = { k, t };
      }
      if (best) return { i: pl.ai[this.vis[best.k]], t: best.t, y: this.yb[best.k], how: 'ray' };
    }
    if (P) {
      let bd = Infinity;
      for (let k = 0; k < this.nVis; k++) {
        const i = this.vis[k], T = pl.tpls[pl.ti[i]], b = T.bbox;
        const reach = Math.max(-b[0], b[3], -b[2], b[5]) + FURN.near_pt_m;
        const d = Math.hypot(P.x - pl.x[i], P.z - pl.z[i]);
        if (d <= reach && d < bd && P.y >= this.yb[k] - 1.5 && P.y <= this.yb[k] + b[4] + 1.5) { bd = d; best = { k }; }
      }
      if (best) return { i: pl.ai[this.vis[best.k]], t: null, y: this.yb[best.k], how: 'near' };
    }
    return null;
  }
}
